import forEach from 'lodash/forEach';

import PropertyConstants from 'app/shared/constants/PropertyConstants';
import Tour from 'app/shared/model/Tour';

const createUnit = unit => ({
    unitId: unit.id,
    name: unit.name || '',
    active: unit.active || false,
    dateAvailable: unit.dateAvailable || '',
    price: unit.lowPrice || '',
    sqft: unit.squareFeet || '',
    tour: new Tour(unit.vrModel),
});

const postingUtils = {
    hasErrors: {
        floorplans(errorObj) {
            let hasError = false;

            forEach(errorObj.floorplans, (floorplan) => {
                for (const prop in floorplan) {
                    if (floorplan.hasOwnProperty(prop) && floorplan[prop]) {
                        hasError = true;
                    }
                }
            });

            return hasError;
        },
        location(errorObj) {
            const hasError = errorObj.street ||
                errorObj.city ||
                errorObj.state ||
                errorObj.zip;

            return hasError;
        },
        detailsAndDesc(errorObj, isCommunity) {
            let hasError = false;

            if (isCommunity) {
                hasError = errorObj.listingTitle || errorObj.description || errorObj.website;
            } else {
                hasError = errorObj.floorplans[0].beds ||
                    errorObj.floorplans[0].baths ||
                    errorObj.description;
            }

            return hasError;
        },
        amenitiesAndRules(errorObj) {
            const hasError = errorObj.laundry ||
                errorObj.petsAllowed.cats ||
                errorObj.petsAllowed.smallDogs ||
                errorObj.petsAllowed.largeDogs ||
                errorObj.petsAllowed.none;

            return hasError;
        },
        contactInfo(errorObj) {
            const hasError = errorObj.contactName ||
                errorObj.contactEmail ||
                errorObj.contactPhone ||
                errorObj.ccToEmail;

            return hasError;
        },
        photos() {
            // Photos aren't required, so disable this check for now.
            // let hasError = errorObj.photos;

            return false;
        },
    },
    validate(formData) {
        const listingAndProperty = this.validateListingAndProperty(formData);
        const location = this.validateLocation(formData);
        const pricingAndAvailability = this.validatePricingAndAvailability(formData);
        const amenitiesAndRules = this.validateAmenitiesAndRules(formData);
        const photos = this.validatePhotos(formData);
        const contactInfo = this.validateContactInfo(formData);
        const floorplans = this.validateFloorplans(formData);
        const detailsAndDesc = formData.isCommunity ?
            this.validateDetailsAndDescMF(formData) : this.validateDetailsAndDesc(formData);

        const all = {
            isValid: false,
            message: null,
        };
        if (listingAndProperty.isValid &&
            location.isValid &&
            pricingAndAvailability.isValid &&
            detailsAndDesc.isValid &&
            amenitiesAndRules.isValid &&
            photos.isValid &&
            contactInfo.isValid) {
            all.isValid = true;
        }

        return {
            listingAndProperty,
            location,
            pricingAndAvailability,
            detailsAndDesc,
            floorplans,
            amenitiesAndRules,
            photos,
            contactInfo,
            all,
        };
    },
    validateListingAndProperty(formData) {
        const isValid = formData.listingType &&
            formData.propertyType;

        return {
            isValid,
            message: null,
        };
    },
    validateLocation(formData) {
        let message = null;
        const isValid = formData.street.length > 0 &&
            formData.city.length > 0 &&
            formData.state.length === 2 &&
            formData.zip.length > 0;

        if (!isValid) {
            message = 'Street, city, state, and zip are required.';
        }

        return {
            isValid,
            message,
        };
    },
    validateFloorplans(formData) {
        let isValid = true;

        forEach(formData.floorplans, (floorplan) => {
            if (floorplan.beds === '' || typeof floorplan.beds === 'undefined') {
                isValid = false;
            }

            if (!floorplan.baths || floorplan.baths === '') {
                isValid = false;
            }
        });

        return {
            isValid,
            message: null,
        };
    },
    validatePricingAndAvailability(formData) {
        const isForSale = formData.listingType === 'sale';
        let isValid = false;

        if (formData.isCommunity) {
            isValid = formData.minLeaseMos;
        } else if (isForSale) {
            isValid = formData.floorplans[0].price;
        } else {
            isValid = formData.floorplans[0].price;
        }

        return {
            isValid,
            message: null,
        };
    },
    validateDetailsAndDesc(formData) {
        const isValid =
            formData.floorplans[0].beds &&
            formData.floorplans[0].price &&
            formData.floorplans[0].baths &&
            formData.leaseDuration;

        return {
            isValid,
            message: null,
        };
    },
    validateDetailsAndDescMF(formData) {
        const isValid = formData.listingTitle &&
            formData.description;
        return {
            isValid,
            message: null,
        };
    },
    validateAmenitiesAndRules(formData) {
        const isValid = (
            formData.laundry &&
            typeof formData.petsAllowed.cats === 'boolean' && formData.petsAllowed.cats !== '' &&
            typeof formData.petsAllowed.smallDogs === 'boolean' && formData.petsAllowed.smallDogs !== '' &&
            typeof formData.petsAllowed.largeDogs === 'boolean' && formData.petsAllowed.largeDogs !== '' &&
            typeof formData.petsAllowed.none === 'boolean' && formData.petsAllowed.none !== '');

        return {
            isValid,
            message: null,
        };
    },
    validatePhotos(formData) {
        const isValid = formData.photos.length > 1;
        return {
            isValid,
            message: null,
        };
    },
    validateContactInfo(formData) {
        const isValid = formData.contactEmail && formData.contactPhone;

        return {
            isValid,
            message: null,
        };
    },
    formatPriceFromApi(lowPrice, highPrice) {
        if (typeof lowPrice === 'undefined') {
            return '';
        }
        if (lowPrice !== highPrice) {
            return `${lowPrice}-${highPrice}`;
        }
        return lowPrice;
    },
    formatBedsFromApi(beds) {
        return (beds >= 0) ? beds : '';
    },
    validateSqFt(value) {
        let userInput = value;

        if (typeof value === 'string') {
            userInput = value.replace(',', '');
        }

        const result = parseInt(userInput, 10);

        if (value === '') {
            return value;
        }
        if (!isNaN(result)) {
            return result;
        }
        return null;
    },
    transformPropertyListingTypeForApi(formData) {
        const { LISTING_CODES_BY_TYPE, PROPERTY_CODES_BY_TYPE } = PropertyConstants;
        return {
            listingTypeCode: LISTING_CODES_BY_TYPE[formData.listingType],
            propertyTypeCode: PROPERTY_CODES_BY_TYPE[formData.propertyType],
            fullAddress: formData.fullAddress,
            unitNumber: formData.unitNumber,
        };
    },
    mapFormDataToApi(field) {
        // Maps form data to specific API endpoints that ultimately update the API.
        // Note, these API endpoints may be different from the actual data that the API returns.
        // e.g., "/api/v1/property/edit/furnished" === propertyObj.furnishedUnitsAvailable
        const mappedData = {
            airConditioning: 'hasAnyAirConditioning',
            amenities: 'propertyAmenities',
            apartmentAmenities: 'apartmentAmenities',
            isApplicationsTogglePristine: 'isApplicationsTogglePristine',
            applicationsAccepted: 'applicationsAccepted',
            isApplicationsAccepted: 'applicationsAccepted',
            balconyOrDeck: 'balconyDeckPatioPorch',
            cats: 'cats',
            ccToEmail: 'email2',
            contactEmail: 'email',
            contactFax: 'fax',
            contactPhone: 'phone',
            contactName: 'contactName',
            contactSunday: 'contactSunday',
            contactMonday: 'contactMonday',
            contactTuesday: 'contactTuesday',
            contactWednesday: 'contactWednesday',
            contactThursday: 'contactThursday',
            contactFriday: 'contactFriday',
            contactSaturday: 'contactSaturday',
            city: 'city',
            description: 'fullDescription',
            floorplans: {
                amenities: 'unitAmenities',
                baths: 'numBathrooms',
                beds: 'numBedrooms',
                dateAvailable: 'dateAvailable',
                floorPlanId: 'id',
                floorPlanPhotos: 'floorPlanPhotos',
                layoutPhotos: 'layout',
                name: 'name',
                active: 'active',
                price: 'price',
                lowPrice: 'lowPrice',
                highPrice: 'highPrice',
                securityDeposit: 'securityDeposit',
                sqft: 'squareFeet',
                units: 'units',
            },
            seattleHousingConnectorStatus: 'seattleHousingConnectorStatus',
            specialOffers: 'specialOffers',
            forRentBy: 'forRentBy',
            furnished: 'isFurnished',
            garageParking: 'garageParking',
            hardwoodFloors: 'hasHardwoodFloorCovering',
            hideAddress: 'hideStreetAddress',
            laundry: 'laundry',
            leaseTerms: 'leaseTerms',
            listingTitle: 'name',
            listingType: 'listingType',
            numUnits: 'unitsInBuilding',
            offStreetParking: 'offStreetParking',
            photos: 'propertyPhotos',
            propertyType: 'propertyType',
            schedule: 'schedule',
            specials: 'previewMessage',
            state: 'state',
            street: 'street',
            unit: 'unit',
            website: 'webSite',
            wheelchairAccess: 'wheelchairAccess',
            zip: 'zip',
        };

        let apiProperty;
        const propertyName = field.split('.');
        if (propertyName[0] === 'floorplans') {
            apiProperty = mappedData.floorplans[propertyName[1]];
        } else {
            apiProperty = mappedData[propertyName[0]];
        }

        return apiProperty;
    },
    transformDatafromApi(apiData) {
        const { LISTING_TYPES_BY_CODE, PROPERTY_TYPES_BY_CODE } = PropertyConstants;
        const dataFromApi = apiData;
        const listingType = LISTING_TYPES_BY_CODE[dataFromApi.listingType.code];
        const propertyType = PROPERTY_TYPES_BY_CODE[dataFromApi.propertyType.code];

        const apiFloorplans = (dataFromApi.floorPlans || []).map(floorplan => ({
            floorPlanId: floorplan.id,
            active: typeof floorplan.active === 'undefined' ? true : floorplan.active,
            layoutPhoto: floorplan.layout || {},
            floorPlanPhotos: floorplan.floorPlanPhotos || [],
            price: this.formatPriceFromApi(floorplan.lowPrice, floorplan.highPrice) || '',
            lowPrice: floorplan.lowPrice || '',
            highPrice: floorplan.highPrice || '',
            securityDeposit: floorplan.securityDeposit,
            baths: floorplan.numBathrooms || '',
            beds: this.formatBedsFromApi(floorplan.numBedrooms),
            sqft: floorplan.squareFeet || '',
            dateAvailable: floorplan.dateAvailable || '',
            name: floorplan.name || '',
            unitAmenities: floorplan.unitAmenities || [],
            units: (floorplan.units || []).map(createUnit),
            tour: new Tour(floorplan.vrModel),
        }));
        const apiUnits = (dataFromApi.units || []).map(createUnit);

        let buildingAttributes = null;
        let airConditioning = null;
        let furnished = null;
        let hardwoodFloors = null;
        if (dataFromApi.hasOwnProperty('buildingAttributes')) {
            buildingAttributes = dataFromApi.buildingAttributes;

            if (buildingAttributes.hasOwnProperty('airConditioningTypes')) {
                airConditioning = buildingAttributes.airConditioningTypes.hasAny;
            }

            if (buildingAttributes.hasOwnProperty('isFurnished')) {
                furnished = buildingAttributes.isFurnished;
            }

            if (buildingAttributes.hasOwnProperty('floorCoveringTypes')) {
                hardwoodFloors = buildingAttributes.floorCoveringTypes.hasHardwood;
            }
        }

        const newFormData = {
            active: dataFromApi.active,
            autoBill: Boolean(dataFromApi.paidInclusion && dataFromApi.paidInclusion.isAutoBillEnabled),
            airConditioning: airConditioning !== null ? airConditioning : dataFromApi.airConditioning,
            aliasEncoded: dataFromApi.aliasEncoded,
            amenities: dataFromApi.propertyAmenities,
            amenitiesText: '',
            apartmentAmenities: dataFromApi.apartmentAmenities,
            archived: dataFromApi.archived,
            canBeArchived: dataFromApi.canBeArchived,
            applicationsAccepted: dataFromApi.applicationsAccepted,
            balconyOrDeck: dataFromApi.balconyDeckPatioPorch,
            buildingAttributes: dataFromApi.buildingAttributes,
            ccToEmail: dataFromApi.email2,
            contactEmail: dataFromApi.email,
            contactFax: dataFromApi.fax,
            contactHours: {
                sunday: dataFromApi.contactHoursSunday,
                monday: dataFromApi.contactHoursMonday,
                tuesday: dataFromApi.contactHoursTuesday,
                wednesday: dataFromApi.contactHoursWednesday,
                thursday: dataFromApi.contactHoursThursday,
                friday: dataFromApi.contactHoursFriday,
                saturday: dataFromApi.contactHoursSaturday,
            },
            contactName: dataFromApi.contactName,
            contactPhone: dataFromApi.phone,
            contactPhoneDateVerified: dataFromApi.datePhoneVerified,
            city: dataFromApi.city,
            dateLastActivated: dataFromApi.dateLastActivated,
            description: dataFromApi.fullDescription,
            expirationDate: dataFromApi.expirationDate,
            feedId: dataFromApi.feedId,
            feedListingId: dataFromApi.feedListingId,
            listingAlias: dataFromApi.listingAlias,
            feedName: dataFromApi.feedName,
            floorplans: apiFloorplans,
            units: apiUnits,
            forRentBy: dataFromApi.forRentBy,
            furnished: furnished !== null ? furnished : dataFromApi.furnishedUnitsAvailable,
            garageParking: dataFromApi.garageParking,
            geo: dataFromApi.geo,
            hardwoodFloors: hardwoodFloors !== null ? hardwoodFloors : dataFromApi.hardwoodFloors,
            hasVrModels: dataFromApi.hasVrModels,
            hideAddress: dataFromApi.hideStreetAddress,
            hidePhone: dataFromApi.hidePhone,
            isAddressValid: dataFromApi.isAddressValid,
            isContactEmailVerified: dataFromApi.isContactEmailVerified,
            isCommunity: dataFromApi.isCommunity,
            isAddressEditable: dataFromApi.isAddressEditable,
            isEditable: dataFromApi.isEditable,
            isFeed: dataFromApi.isFeed,
            isViewable: dataFromApi.isViewable,
            isMultifamily: dataFromApi.isMultifamily,
            isPaidMultifamily: dataFromApi.isPaidMultifamily,
            isApplicationsTogglePristine: dataFromApi.isApplicationsTogglePristine,
            isApplicationsAccepted: dataFromApi.isApplicationsAccepted,
            isPaymentsEnabled: dataFromApi.isPaymentsEnabled,
            isPopulatedFromBuildingAttributeDatabase: dataFromApi.isPopulatedFromBuildingAttributeDatabase,
            laundry: dataFromApi.laundry,
            leaseDuration: dataFromApi.leaseDuration,
            leaseTerms: dataFromApi.leaseTerms,
            listingId: dataFromApi.listingId,
            listingTitle: dataFromApi.name,
            listingType,
            listingTypeCode: dataFromApi.listingType.code,
            logo: dataFromApi.logo,
            numUnits: dataFromApi.numUnits,
            numLeadsSinceLastActivated: dataFromApi.numLeadsSinceLastActivated,
            isActivationBlocked: dataFromApi.isActivationBlocked,
            seattleHousingConnectorStatus: dataFromApi.seattleHousingConnectorStatus,
            offStreetParking: dataFromApi.offStreetParking,
            paidInclusion: dataFromApi.paidInclusion,
            localeSpecificNotice: dataFromApi.localeSpecificNotice,
            permanent: dataFromApi.permanent,
            petsAllowed: dataFromApi.petsAllowed,
            photos: dataFromApi.propertyPhotos,
            propertyTypeCode: dataFromApi.propertyType.code,
            propertyType,
            rentZestimate: dataFromApi.rentZestimate,
            restrictions: dataFromApi.restrictions,
            schedule: dataFromApi.schedule,
            specials: dataFromApi.previewMessage,
            specialOffers: dataFromApi.specialOffers,
            state: dataFromApi.state,
            street: dataFromApi.street,
            syndication: dataFromApi.syndication,
            topDupesDetails: dataFromApi.topDupesDetails,
            trackingPhoneNumber: dataFromApi.trackingPhoneNumber,
            unit: dataFromApi.unit,
            vaultware: dataFromApi.vaultware,
            vaultwarePropertyId: dataFromApi.vaultwarePropertyId,
            verified: !dataFromApi.needsPhoneVerification,
            website: dataFromApi.website,
            wheelchairAccess: dataFromApi.wheelchairAccess,
            zip: dataFromApi.zip,
            tour: new Tour(dataFromApi.vrModel),
        };
        return newFormData;
    },
};

export default postingUtils;
